/**
 * The session: who is signed in, whether they may write, and the token the API
 * client asks for on every request.
 *
 * A Pinia store rather than a module singleton because the shell, the guard
 * and the pages all read it reactively. It holds no token and knows nothing
 * about expiry — MSAL owns storage, decoding and renewal, so this store only
 * forwards a request for a bearer value and never renders or logs one
 * (PO-TI-022-00).
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { ApiError, apiFetch } from '../api/client.js'
import type { Me } from '../types/app.types.js'
import { authErrorCode, TERMINAL_AUTH_ERROR_CODES, type AuthErrorCode } from './errors.js'
import type { AuthProvider, AuthenticatedUser } from './types.js'

/**
 * Picks the provider once, at module load.
 *
 * The dynamic import is what keeps `msal.mock.ts` out of a production bundle:
 * `import.meta.env.VITE_AUTH_MOCK` is inlined as a literal at build time, so
 * Rollup drops the branch that is not taken. A static import of both would ship
 * a module that fabricates sessions to production. The same idiom selects the
 * MSW handlers in `index.ts` and mercury's `stores/auth.ts` uses it for exactly
 * this pair of modules.
 */
let resolved: AuthProvider | null = null

const loading: Promise<AuthProvider> = (async () => {
  resolved =
    import.meta.env.VITE_AUTH_MOCK === 'true'
      ? await import('./msal.mock.js')
      : await import('./msal.js')
  return resolved
})()

/**
 * The provider, awaited once and then reused. Six call sites below need the
 * same "already loaded, or wait for the one in-flight load" behaviour.
 */
async function provider(): Promise<AuthProvider> {
  return resolved ?? loading
}

export const useSession = defineStore('session', () => {
  const user = ref<AuthenticatedUser | null>(null)
  /** The API's answer, never a local guess. `GET /api/me` decides. */
  const canWrite = ref(false)
  /** False until MSAL has been consulted; the router waits for it. */
  const ready = ref(false)
  const pending = ref(false)
  const error = ref<AuthErrorCode | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const fullName = computed(() => user.value?.displayName ?? '')
  /** What actually identifies a company account, and what the shell shows. */
  const email = computed(() => user.value?.email ?? '')

  function clear(): void {
    user.value = null
    canWrite.value = false
  }

  /**
   * The bearer token for the next request. Registered as `api/client.ts`'s
   * token provider.
   *
   * No expiry check here: `acquireTokenSilent` serves its cache until the
   * token is near expiry and renews it otherwise, and it coalesces concurrent
   * calls itself.
   */
  async function getAccessToken(): Promise<string | undefined> {
    const msal = await provider()
    return (await msal.getAccessToken()) ?? undefined
  }

  /**
   * Loads `canWrite` — and the authoritative user record — from the API.
   *
   * Three outcomes, because they need three different reactions. A code the
   * API marks terminal (`TENANT_FORBIDDEN`, `IDENTITY_EMAIL_CONFLICT`) means
   * the credential is fine and the identity is not, so renewing can never help
   * and the cached account has to go with the session. A 401 or 403 without
   * such a code ends the session and lets the person sign in again. Anything
   * else — a 5xx, a proxy, an offline laptop — leaves whatever session exists
   * standing with `canWrite` false, because the API being briefly unreachable
   * should not sign anyone out and read-only is the safe side to fail to.
   *
   * On a cold start there is nothing to leave standing: `id`, `email` and
   * `role` exist only in this response, and MSAL's account is not a substitute
   * for them. So a first load that cannot reach the API ends on the login page
   * with `ME_UNAVAILABLE`, which is why that message promises nothing about
   * read-only — the shell says that, off `canWrite`.
   */
  async function loadMe(): Promise<void> {
    try {
      const me = await apiFetch<Me>('/me')
      canWrite.value = me.canWrite
      user.value = {
        id: me.id,
        azureOid: me.azureOid,
        email: me.email,
        displayName: me.displayName,
        role: me.role,
      }
      error.value = null
    } catch (cause) {
      canWrite.value = false
      const code = authErrorCode(cause)

      // The code is read before the status: the conflict answers 409, which is
      // neither of the statuses `isAuthFailure` covers, yet it is as terminal
      // as a rejected tenant.
      if (code !== null && TERMINAL_AUTH_ERROR_CODES.includes(code)) {
        clear()
        error.value = code
        await (await provider()).forgetAccount()
        return
      }

      if (cause instanceof ApiError && cause.isAuthFailure) {
        clear()
        error.value = code
        return
      }

      error.value = 'ME_UNAVAILABLE'
    }
  }

  /**
   * Hands the document to Entra. There is no password to collect: on success
   * this never returns, because the page is replaced.
   *
   * `pending` is deliberately left set on the happy path — the button should
   * keep its spinner while the browser navigates — and the failure is exposed
   * through `error` rather than thrown, so the login page has one place to
   * read from whether the failure happened here or on the way back.
   */
  async function signIn(): Promise<void> {
    pending.value = true
    error.value = null
    try {
      await (await provider()).signIn()
    } catch (cause) {
      pending.value = false
      error.value = authErrorCode(cause) ?? 'REDIRECT_FAILED'
    }
  }

  async function logout(): Promise<void> {
    clear()
    error.value = null
    // Redirects to Entra's logout endpoint and back, which is the point: only
    // that ends the directory session, and dropping local state alone would
    // let the next sign-in complete without a prompt.
    await (await provider()).signOut()
  }

  /**
   * Resolves the session before the router mounts. Awaited by `installAuth`,
   * so a hard refresh never bounces an authenticated user to the login page.
   *
   * `handleRedirectResult` runs unconditionally and first: it is what consumes
   * the authorization code Entra appended to the URL, and MSAL has no account
   * to report until it has. Only afterwards does an existing account mean
   * "signed in".
   */
  async function restore(): Promise<void> {
    try {
      const msal = await provider()
      await msal.ensureInitialized()
      await msal.handleRedirectResult()
      if (msal.getAccount() !== null) await loadMe()
    } catch (cause) {
      clear()
      error.value = authErrorCode(cause) ?? 'REDIRECT_FAILED'
    } finally {
      // Set even on failure: a session that never resolves would hang the
      // router forever instead of showing the login page and the reason.
      ready.value = true
    }
  }

  /**
   * Called by `api/client.ts` when a request comes back 401.
   *
   * One silent attempt, then the session ends. The client does not retry the
   * failed request (it is frozen and deliberately simple), so the caller still
   * sees that one error — but a token that merely aged out between MSAL's
   * cache check and the API's clock costs no more than a renewal, and evicting
   * the session for it would lose unsaved editor state.
   */
  async function handleAuthFailure(): Promise<void> {
    const msal = await provider()
    if ((await msal.getAccessToken()) === null) {
      clear()
      await msal.forgetAccount()
    }
  }

  return {
    user,
    canWrite,
    ready,
    pending,
    error,
    isAuthenticated,
    fullName,
    email,
    signIn,
    logout,
    restore,
    getAccessToken,
    handleAuthFailure,
  }
})
